import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PayoutService, AffiliateCommission, AffiliateCode } from '../../services/payout.service';
import { PayoutInfo, PayoutRequest, Payout } from '../../interfaces/payout';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-payout',
  standalone: true,
  imports: [CommonModule, TranslateModule, SkeletonComponent, ButtonComponent],
  templateUrl: './payout.component.html',
  styleUrl: './payout.component.scss'
})
export class PayoutComponent implements OnInit {
  // Loading states
  isLoadingInfo = false;
  isLoadingCodes = false;
  isLoadingCommissions = false;
  isLoadingRequests = false;
  isLoadingPayouts = false;
  isSubmitting = false;

  // Data
  payoutInfo: PayoutInfo | null = null;
  codes: AffiliateCode[] = [];
  commissions: AffiliateCommission[] = [];
  payoutRequests: PayoutRequest[] = [];
  payouts: Payout[] = [];

  // Errors & messages
  errorInfo = '';
  errorSubmit = '';
  successMessage = '';

  // Active tab
  activeTab: 'commissions' | 'requests' | 'payouts' = 'commissions';

  constructor(
    private payoutService: PayoutService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadPayoutInfo();
    this.loadCodes();
    this.loadCommissions();
    this.loadPayoutRequests();
    this.loadPayouts();
  }

  loadPayoutInfo(): void {
    this.isLoadingInfo = true;
    this.errorInfo = '';
    this.payoutService.getPayoutInfo().subscribe({
      next: (info) => { this.payoutInfo = info; this.isLoadingInfo = false; },
      error: () => { this.isLoadingInfo = false; this.errorInfo = 'Error al cargar la información.'; }
    });
  }

  loadCodes(): void {
    this.isLoadingCodes = true;
    this.payoutService.getMyCodes().subscribe({
      next: (codes) => { this.codes = codes; this.isLoadingCodes = false; },
      error: () => { this.isLoadingCodes = false; }
    });
  }

  loadCommissions(): void {
    this.isLoadingCommissions = true;
    this.payoutService.getMyCommissions().subscribe({
      next: (commissions) => { this.commissions = commissions; this.isLoadingCommissions = false; },
      error: () => { this.isLoadingCommissions = false; }
    });
  }

  loadPayoutRequests(): void {
    this.isLoadingRequests = true;
    this.payoutService.getPayoutHistory().subscribe({
      next: (requests) => {
        this.payoutRequests = requests.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        this.isLoadingRequests = false;
      },
      error: () => { this.isLoadingRequests = false; }
    });
  }

  loadPayouts(): void {
    this.isLoadingPayouts = true;
    this.payoutService.getMyPayouts().subscribe({
      next: (payouts) => { this.payouts = payouts; this.isLoadingPayouts = false; },
      error: () => { this.isLoadingPayouts = false; }
    });
  }

  setActiveTab(tab: 'commissions' | 'requests' | 'payouts'): void {
    this.activeTab = tab;
  }

  submitPayoutRequest(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.errorSubmit = '';
    this.successMessage = '';
    this.payoutService.submitPayoutRequest().subscribe({
      next: () => {
        this.isSubmitting = false;
        this.successMessage = 'Solicitud de cobro enviada exitosamente.';
        this.loadPayoutInfo();
        this.loadPayoutRequests();
      },
      error: (err) => {
        this.isSubmitting = false;
        if (err.status === 409) {
          this.errorSubmit = 'Ya existe una solicitud de cobro pendiente.';
          this.loadPayoutInfo();
        } else if (err.status === 400) {
          this.errorSubmit = 'Saldo insuficiente para solicitar un cobro.';
        } else if (err.status === 403) {
          this.errorSubmit = 'Tu cuenta no es elegible para solicitar cobros.';
        } else {
          this.errorSubmit = 'Error al enviar la solicitud. Intenta nuevamente.';
        }
      }
    });
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`payout.status.${status}`) || status;
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'pending':   return 'bg-[#FFF3E0] text-[var(--secondary)]';
      case 'available': return 'bg-[#E8F5E9] text-[#2E7D32]';
      case 'paid':      return 'bg-[#E3F2FD] text-[var(--blue)]';
      case 'cancelled': return 'bg-[#FFEBEE] text-[var(--bordeaux)]';
      case 'approved':  return 'bg-[#E8F5E9] text-[#2E7D32]';
      case 'rejected':  return 'bg-[#FFEBEE] text-[var(--bordeaux)]';
      default:          return 'bg-[var(--light-grey)] text-[var(--dark-grey)]';
    }
  }

  getOriginLabel(type: string): string {
    return type === 'payout_request'
      ? this.translate.instant('payout.originRequest')
      : this.translate.instant('payout.originDirect');
  }

  getOriginColor(type: string): string {
    return type === 'payout_request'
      ? 'bg-[#E8F5E9] text-[#2E7D32]'
      : 'bg-[#E3F2FD] text-[var(--blue)]';
  }

  getCommissionLabel(code: AffiliateCode): string {
    if (!code.commission_type || code.commission_value == null) return '-';
    if (code.commission_type === 'percentage') return `${code.commission_value}%`;
    return `$${Number(code.commission_value).toFixed(2)}`;
  }

  get activeCodes(): AffiliateCode[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.codes.filter(c => {
      if (!c.isActive) return false;
      const exp = new Date(c.expirationDate);
      exp.setHours(0, 0, 0, 0);
      return exp >= today;
    });
  }
}
